import React, { useState, useCallback, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/free-solid-svg-icons"
import { ExcelTab } from "types/ReportingDashboard/reporting.types"

/**
 * Props for the editable tab title component
 */
interface ReportTabTitleProps {
  /** Tab configuration item */
  tabConfigItem: ExcelTab
  /** Callback to update tab label */
  onLabelUpdate: (tabKey: string, newLabel: string) => void
}

/**
 * Editable tab title component for report tables
 */
const ReportTabTitle: React.FC<ReportTabTitleProps> = ({
  tabConfigItem,
  onLabelUpdate,
}) => {
  const [editing, setEditing] = useState<boolean>(false)
  const [title, setTitle] = useState<string>(tabConfigItem.label)

  useEffect(() => {
    setTitle(tabConfigItem.label)
  }, [tabConfigItem.label])

  const handleEditClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    setEditing(true)
  }, [])

  const saveTitle = useCallback((): void => {
    setEditing(false)
    const trimmedTitle = title.trim()
    onLabelUpdate(
      tabConfigItem.key,
      trimmedTitle || `Table ${tabConfigItem.key}`
    )
  }, [tabConfigItem.key, title, onLabelUpdate])

  const handleInputBlur = useCallback((): void => saveTitle(), [saveTitle])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Enter") saveTitle()
      else if (e.key === "Escape") {
        setTitle(tabConfigItem.label)
        setEditing(false)
      }
    },
    [saveTitle, tabConfigItem.label]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => setTitle(e.target.value),
    []
  )

  const handleInputClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>): void => e.stopPropagation(),
    []
  )

  if (editing) {
    return (
      <input
        ref={(input) => {
          if (input) {
            input.focus()
          }
        }}
        className="w-24 h-6 px-1 border rounded text-sm"
        value={title}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        onClick={handleInputClick}
        aria-label={`Edit tab name for ${tabConfigItem.label}`}
      />
    )
  }
  return (
    <span>
      {tabConfigItem.label}
      <FontAwesomeIcon
        icon={faPen}
        className="ml-2 text-xs cursor-pointer text-gray-400 hover:text-blue-500"
        onClick={handleEditClick}
        title="Edit Tab Name"
        role="button"
        aria-label={`Edit name for tab ${tabConfigItem.label}`}
      />
    </span>
  )
}

export default ReportTabTitle
